import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { ChevronDown, Camera, Eye, EyeOff, Globe, Loader2, Lock, LogOut, Plus, RefreshCw, Star, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiErrorDetail, sessionDateLabel } from "@/components/forms";

const api = axios.create({ baseURL: API, withCredentials: true });

async function callWithRefresh(fn) {
  try {
    return await fn();
  } catch (e) {
    if (e.response && e.response.status === 401) {
      await api.post("/auth/refresh");
      return fn();
    }
    throw e;
  }
}

const SUBMISSION_TABS = [
  { key: "applications", label: "Applications", nameField: "athlete_name", emailField: "athlete_email" },
  { key: "prospects", label: "Recruit Pipeline", nameField: "full_name", emailField: "parent_email" },
  { key: "contact_messages", label: "Messages", nameField: "full_name", emailField: "email" },
  { key: "notify_signups", label: "Alert Signups", nameField: "email", emailField: "email" },
  { key: "camp_registrations", label: "Camp Registrations", nameField: "full_name", emailField: "email" },
];

const STAGES = [
  { key: "new", label: "New", dot: "bg-slate-400", border: "border-l-slate-500" },
  { key: "contacted", label: "Contacted", dot: "bg-ice", border: "border-l-ice" },
  { key: "evaluating", label: "Evaluating", dot: "bg-amber-400", border: "border-l-amber-400" },
  { key: "visit", label: "Visit Set", dot: "bg-violet-400", border: "border-l-violet-400" },
  { key: "committed", label: "Committed", dot: "bg-emerald-400", border: "border-l-emerald-400" },
  { key: "passed", label: "Passed", dot: "bg-slate-600", border: "border-l-slate-600" },
];

const LABELS = {
  athlete_name: "Athlete Name",
  athlete_phone: "Athlete Phone",
  athlete_email: "Athlete Email",
  high_school: "High School",
  position: "Position",
  height: "Height",
  weight: "Weight",
  parent_name: "Parent Name",
  parent_phone: "Parent Phone",
  parent_email: "Parent Email",
  full_name: "Full Name",
  graduation_year: "Grad Year",
  school: "School",
  film_link: "Film Link",
  film_file_name: "Film File",
  phone: "Phone",
  email: "Email",
  role_type: "Role",
  preferred_contact: "Reply By",
  message: "Message",
  interested_in: "Interested In",
  camp: "Camp",
  session_title: "Session",
  created_at: "_",
  id: "_",
  is_read: "_",
  stage: "_",
  stars: "_",
  notes: "_",
  profile: "_",
};

const SKIP_KEYS = new Set(["id", "created_at", "is_read", "stage", "stars", "notes", "profile"]);

const CAMP_OPTIONS = [
  { key: "showcase", label: "Post-Grad Showcase Camp", badge: "For post-grad athletes" },
  { key: "highschool", label: "High School Camp", badge: "HS juniors & seniors" },
  { key: "community", label: "Community Camp", badge: "Tuskegee community" },
];

function fmtDateTime(iso) {
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso || "";
  }
}

const api_input =
  "w-full rounded-lg border bg-white/[0.04] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice/70";

/* ------------------------------------------------------------------ */

function LoginScreen({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      toast.success(`Welcome back, Coach.`);
      onAuthed(data.email);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center px-5 py-32">
      <div className="glass w-full max-w-md rounded-2xl p-8" data-testid="admin-login-card">
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl border border-ice/30 bg-ice/10 text-ice">
            <Lock className="h-5 w-5" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-extrabold uppercase text-white">Staff Login</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
              Alabama Bulldogs Sports Academy
            </p>
          </div>
        </div>
        <form onSubmit={submit} noValidate data-testid="admin-login-form" className="mt-8 space-y-5">
          <label className="block">
            <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">
              Email
            </span>
            <input
              data-testid="admin-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="staff email"
              className={`${api_input} border-white/10`}
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">
              Password
            </span>
            <input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••••"
              className={`${api_input} border-white/10`}
            />
          </label>
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={sending}
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-crimson px-7 py-3.5 font-display text-base font-bold uppercase tracking-wider text-white transition-colors hover:bg-crimson-bright active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice disabled:opacity-60"
          >
            {sending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Checking
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function SubmissionCard({ type, item, meta, onToggleRead, onDelete }) {
  const [open, setOpen] = useState(false);
  const rows = Object.entries(item).filter(([k]) => !SKIP_KEYS.has(k) && item[k] !== "" && item[k] != null);
  return (
    <div
      data-testid={`submission-card-${item.id}`}
      className={`rounded-xl border transition-colors ${
        item.is_read ? "border-white/5 bg-navy-850" : "border-ice/30 bg-navy-800"
      }`}
    >
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid={`submission-expand-${item.id}`}
        className="flex w-full items-center gap-4 px-5 py-4 text-left"
      >
        <span
          className={`h-2 w-2 shrink-0 rounded-full ${item.is_read ? "bg-slate-600" : "bg-crimson animate-pulse-dot"}`}
        />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-display text-lg font-bold uppercase tracking-wide text-white">
            {item[meta.nameField] || "(no name)"}
          </span>
          <span className="mt-0.5 block truncate font-mono text-[11px] text-slate-400">
            {item[meta.emailField] || ""} · {fmtDateTime(item.created_at)}
          </span>
        </span>
        {!item.is_read && (
          <span className="rounded-full bg-crimson/15 px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-crimson-bright">
            New
          </span>
        )}
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="border-t border-white/5 px-5 py-4" data-testid={`submission-detail-${item.id}`}>
          <dl className="space-y-2">
            {rows.map(([k, v]) => (
              <div key={k} className="flex flex-wrap gap-x-4 gap-y-0.5 text-sm">
                <dt className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500" style={{ width: 110, paddingTop: 3 }}>
                  {LABELS[k] || k.replace(/_/g, " ")}
                </dt>
                <dd className="min-w-0 flex-1 break-words text-slate-200">
                  {k === "film_link" ? (
                    <a href={v} target="_blank" rel="noreferrer" className="break-all text-ice transition-colors hover:text-white">
                      {v}
                    </a>
                  ) : k === "film_file_url" ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${v}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-ice transition-colors hover:text-white"
                    >
                      Open uploaded file
                    </a>
                  ) : (
                    String(v)
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-4 flex gap-3">
            <button
              data-testid={`submission-read-toggle-${item.id}`}
              onClick={() => onToggleRead(item)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-silver-dim transition-colors hover:border-ice/60 hover:text-ice"
            >
              {item.is_read ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
              Mark {item.is_read ? "Unread" : "Read"}
            </button>
            <button
              data-testid={`submission-delete-btn-${item.id}`}
              onClick={() => onDelete(item)}
              className="inline-flex items-center gap-2 rounded-full border border-crimson/40 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-crimson-bright transition-colors hover:bg-crimson/10"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Recruit pipeline                                                    */
/* ------------------------------------------------------------------ */

function StarRating({ value, onChange, testid }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={testid}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          data-testid={`${testid}-btn-${n}`}
          onClick={() => onChange(n === value ? 0 : n)}
          aria-label={`Rate ${n} star${n > 1 ? "s" : ""}`}
          className="transition-transform hover:scale-110 focus-visible:outline-none"
        >
          <Star className={`h-4 w-4 ${n <= value ? "fill-amber-400 text-amber-400" : "text-slate-600"}`} />
        </button>
      ))}
    </div>
  );
}

function ProfileForm({ p, onSaved }) {
  const prof = p.profile || { published: false, jersey: "", bio: "", photo_url: "", stats: [], career_table: { headers: [], rows: [] }, qa: [] };
  const [form, setForm] = useState({
    published: !!prof.published,
    jersey: prof.jersey || "",
    bio: prof.bio || "",
    stats: (prof.stats || []).map((s) => ({ ...s })),
    tableHeaders: (prof.career_table?.headers || []).join(", "),
    tableRows: (prof.career_table?.rows || []).map((r) => r.join(", ")).join("\n"),
    qa: (prof.qa || []).map((q) => ({ ...q })),
  });
  const [photo, setPhoto] = useState(prof.photo_url || "");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const prof2 = p.profile || {};
    setForm({
      published: !!prof2.published,
      jersey: prof2.jersey || "",
      bio: prof2.bio || "",
      stats: (prof2.stats || []).map((s) => ({ ...s })),
      tableHeaders: (prof2.career_table?.headers || []).join(", "),
      tableRows: (prof2.career_table?.rows || []).map((r) => r.join(", ")).join("\n"),
      qa: (prof2.qa || []).map((q) => ({ ...q })),
    });
    setPhoto(prof2.photo_url || "");
  }, [p.id, p.profile]);

  const setStat = (id, key, val) =>
    setForm((f) => ({ ...f, stats: f.stats.map((s) => (s.id === id ? { ...s, [key]: val } : s)) }));
  const addStat = () =>
    setForm((f) => ({
      ...f,
      stats: [...f.stats, { id: Math.random().toString(36).slice(2, 10), label: "", value: "" }],
    }));
  const removeStat = (id) => setForm((f) => ({ ...f, stats: f.stats.filter((s) => s.id !== id) }));

  const setQA = (id, key, val) =>
    setForm((f) => ({ ...f, qa: f.qa.map((q) => (q.id === id ? { ...q, [key]: val } : q)) }));
  const addQA = () =>
    setForm((f) => ({
      ...f,
      qa: [...f.qa, { id: Math.random().toString(36).slice(2, 10), question: "", answer: "" }],
    }));
  const removeQA = (id) => setForm((f) => ({ ...f, qa: f.qa.filter((q) => q.id !== id) }));

  const uploadPhoto = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Headshots must be an image (JPG, PNG or WebP).");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("That image is too large — 10MB max.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await callWithRefresh(() =>
        api.post("/uploads/headshot", fd, { headers: { "Content-Type": "multipart/form-data" } })
      );
      setPhoto(data.url);
      toast.success("Headshot uploaded — hit Save Profile to publish it.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        published: form.published,
        jersey: form.jersey.trim(),
        bio: form.bio.trim(),
        photo_url: photo,
        stats: form.stats
          .filter((s) => s.label.trim() && s.value.trim())
          .slice(0, 12)
          .map((s) => ({ label: s.label.trim(), value: s.value.trim() })),
        career_table_headers: form.tableHeaders,
        career_table_rows: form.tableRows,
        qa: form.qa
          .filter((q) => q.question.trim() && q.answer.trim())
          .slice(0, 12)
          .map((q) => ({ question: q.question.trim(), answer: q.answer.trim() })),
      };
      const { data } = await callWithRefresh(() => api.put(`/admin/prospects/${p.id}/profile`, payload));
      onSaved(data);
      toast.success(data.published ? "Public profile saved and live on the Roster page." : "Public profile saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={save} noValidate data-testid={`profile-form-${p.id}`} className="rounded-xl border border-white/10 bg-navy-850 p-4">
      <p className="flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-wider text-white">
        <Globe className="h-4 w-4 text-ice" /> Public Profile
      </p>
      <p className="mt-1 text-[11px] leading-snug text-slate-500">
        Shows on the Roster page with film and stats — available once the recruit is Committed. Parent contact and
        staff notes always stay private.
      </p>

      <div className="mt-4 flex items-center gap-4">
        {photo ? (
          <img
            data-testid={`profile-photo-preview-${p.id}`}
            src={`${process.env.REACT_APP_BACKEND_URL}${photo}`}
            alt="Headshot preview"
            className="h-16 w-16 rounded-2xl border border-ice/30 object-cover"
          />
        ) : (
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl border border-dashed border-white/20 bg-navy-800 font-display text-lg font-black text-slate-500">
            {p.full_name
              .split(" ")
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0].toUpperCase())
              .join("")}
          </div>
        )}
        <div className="flex flex-col items-start gap-2">
          <input
            type="file"
            accept="image/*"
            data-testid={`profile-photo-input-${p.id}`}
            onChange={uploadPhoto}
            disabled={uploading}
            className="hidden"
            id={`profile-photo-${p.id}`}
          />
          <label
            htmlFor={`profile-photo-${p.id}`}
            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-white/15 px-4 py-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-silver-dim transition-colors hover:border-ice/60 hover:text-ice"
          >
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Camera className="h-3 w-3" />}
            {uploading ? "Uploading…" : photo ? "Change photo" : "Upload headshot"}
          </label>
          {photo && (
            <button
              type="button"
              data-testid={`profile-photo-remove-${p.id}`}
              onClick={() => setPhoto("")}
              className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500 transition-colors hover:text-crimson-bright"
            >
              Remove photo
            </button>
          )}
          <span className="font-mono text-[8px] uppercase tracking-[0.12em] text-slate-600">
            JPG or PNG · 10MB max · square looks best
          </span>
        </div>
      </div>

      <label className="mt-4 flex items-center gap-3">
        <input
          type="checkbox"
          data-testid={`profile-publish-toggle-${p.id}`}
          checked={form.published}
          onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))}
          className="h-4 w-4 accent-[#38BDF8]"
        />
        <span className="text-sm text-slate-300">Published on the Roster page</span>
      </label>

      <div className="mt-3 grid grid-cols-[80px_1fr] gap-3">
        <label className="block">
          <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Jersey</span>
          <input
            data-testid={`profile-jersey-input-${p.id}`}
            value={form.jersey}
            onChange={(e) => setForm((f) => ({ ...f, jersey: e.target.value }))}
            placeholder="#7"
            className={`${api_input} border-white/10 !px-3 !py-2 !text-sm`}
          />
        </label>
        <label className="block">
          <span className="mb-1 block font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Headline / bio</span>
          <textarea
            data-testid={`profile-bio-input-${p.id}`}
            rows={2}
            value={form.bio}
            onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
            placeholder="One or two lines on the player — style of play, accolades, what coaches should know."
            className={`${api_input} resize-none border-white/10 !px-3 !py-2 !text-sm`}
          />
        </label>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Measurable stats</span>
          <button
            type="button"
            data-testid={`profile-add-stat-${p.id}`}
            onClick={addStat}
            className="inline-flex items-center gap-1 rounded-full border border-ice/40 px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-ice transition-colors hover:bg-ice hover:text-navy-900"
          >
            <Plus className="h-3 w-3" /> Stat
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {form.stats.map((s) => (
            <div key={s.id} className="flex items-center gap-2">
              <input
                data-testid={`profile-stat-label-${p.id}`}
                value={s.label}
                onChange={(e) => setStat(s.id, "label", e.target.value)}
                placeholder="40 YD"
                className={`${api_input} border-white/10 !px-3 !py-1.5 !text-xs`}
              />
              <input
                data-testid={`profile-stat-value-${p.id}`}
                value={s.value}
                onChange={(e) => setStat(s.id, "value", e.target.value)}
                placeholder="4.58"
                className={`${api_input} border-white/10 !px-3 !py-1.5 !text-xs`}
              />
              <button
                type="button"
                onClick={() => removeStat(s.id)}
                aria-label="Remove stat"
                className="shrink-0 text-slate-500 transition-colors hover:text-crimson-bright"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
          {form.stats.length === 0 && (
            <p className="text-[11px] text-slate-500">No stats yet — add measurables like 40 YD, GPA, PPG.</p>
          )}
        </div>
      </div>

      <div className="mt-4 border-t border-white/5 pt-4">
        <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Career stats table</p>
        <label className="mt-2 block">
          <span className="mb-1 block text-[11px] text-slate-500">Column headers (comma-separated)</span>
          <input
            data-testid={`profile-table-headers-${p.id}`}
            value={form.tableHeaders}
            onChange={(e) => setForm((f) => ({ ...f, tableHeaders: e.target.value }))}
            placeholder="SEASON, GP, REC, YDS, TD"
            className={`${api_input} border-white/10 !px-3 !py-2 !text-xs`}
          />
        </label>
        <label className="mt-2 block">
          <span className="mb-1 block text-[11px] text-slate-500">Rows — one per line, comma-separated</span>
          <textarea
            data-testid={`profile-table-rows-${p.id}`}
            rows={3}
            value={form.tableRows}
            onChange={(e) => setForm((f) => ({ ...f, tableRows: e.target.value }))}
            placeholder={"2026, 10, 58, 1120, 14\n2025, 11, 41, 780, 6"}
            className={`${api_input} resize-none border-white/10 !px-3 !py-2 font-mono !text-xs`}
          />
        </label>
      </div>

      <div className="mt-4 border-t border-white/5 pt-4">
        <div className="flex items-center justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">Q&A</p>
          <button
            type="button"
            data-testid={`profile-qa-add-${p.id}`}
            onClick={addQA}
            className="inline-flex items-center gap-1 rounded-full border border-ice/40 px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-ice transition-colors hover:bg-ice hover:text-navy-900"
          >
            <Plus className="h-3 w-3" /> Q&A
          </button>
        </div>
        <div className="mt-2 space-y-2">
          {form.qa.map((q) => (
            <div key={q.id} className="rounded-lg border border-white/10 bg-navy-800 p-3">
              <div className="flex items-center gap-2">
                <input
                  data-testid={`profile-qa-question-${q.id}`}
                  value={q.question}
                  onChange={(e) => setQA(q.id, "question", e.target.value)}
                  placeholder="Question — e.g. Who's your role model?"
                  className={`${api_input} border-white/10 !px-3 !py-1.5 !text-xs`}
                />
                <button
                  type="button"
                  onClick={() => removeQA(q.id)}
                  aria-label="Remove Q&A"
                  className="shrink-0 text-slate-500 transition-colors hover:text-crimson-bright"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <textarea
                data-testid={`profile-qa-answer-${q.id}`}
                rows={2}
                value={q.answer}
                onChange={(e) => setQA(q.id, "answer", e.target.value)}
                placeholder="Answer…"
                className={`${api_input} mt-2 resize-none border-white/10 !px-3 !py-1.5 !text-xs`}
              />
            </div>
          ))}
          {form.qa.length === 0 && (
            <p className="text-[11px] text-slate-500">No Q&A yet — add interview-style questions for the profile page.</p>
          )}
        </div>
      </div>

      <button type="submit" data-testid={`profile-save-btn-${p.id}`} disabled={sending} className="btn-primary mt-4 !px-5 !py-2 !text-xs">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Profile"}
      </button>
    </form>
  );
}

function ProspectCard({ p, stageMeta, onPatch, onAddNote, onDeleteNote, onDelete, onSetProfile }) {
  const [open, setOpen] = useState(false);
  const [noteText, setNoteText] = useState("");

  const addNote = async (e) => {
    e.preventDefault();
    if (!noteText.trim()) {
      toast.error("Write a note first.");
      return;
    }
    await onAddNote(p.id, noteText.trim());
    setNoteText("");
  };

  const rows = Object.entries(p).filter(([k]) => !SKIP_KEYS.has(k) && p[k] !== "" && p[k] != null);

  return (
    <div
      data-testid={`prospect-card-${p.id}`}
      className={`rounded-xl border border-white/10 border-l-4 bg-navy-800 ${stageMeta.border} ${
        p.stage === "passed" ? "opacity-60" : ""
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid={`prospect-expand-${p.id}`}
            className="min-w-0 flex-1 text-left"
          >
            <span className="block truncate font-display text-lg font-bold uppercase leading-tight tracking-wide text-white">
              {p.full_name}
            </span>
            <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">
              {p.position} · {p.school} · {p.graduation_year}
            </span>
          </button>
          {p.profile?.published && (
            <span className="mt-0.5 flex shrink-0 items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 font-mono text-[8px] font-semibold uppercase tracking-[0.14em] text-emerald-300">
              <Globe className="h-2.5 w-2.5" /> Live
            </span>
          )}
        </div>

        <div className="mt-3">
          <StarRating
            value={p.stars}
            onChange={(n) => onPatch(p.id, { stars: n })}
            testid={`prospect-star-${p.id}`}
          />
        </div>

        <select
          data-testid={`prospect-stage-select-${p.id}`}
          value={p.stage}
          onChange={(e) => onPatch(p.id, { stage: e.target.value })}
          className="mt-3 w-full appearance-none rounded-lg border border-white/10 bg-navy-700 px-3 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice/70"
        >
          {STAGES.map((s) => (
            <option key={s.key} value={s.key} className="bg-navy-800 text-white">
              {s.label}
            </option>
          ))}
        </select>
      </div>

      {open && (
        <div className="space-y-4 border-t border-white/5 p-4" data-testid={`prospect-detail-${p.id}`}>
          <dl className="space-y-1.5">
            {rows.map(([k, v]) => (
              <div key={k} className="flex flex-wrap gap-x-3 text-xs">
                <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500" style={{ width: 92, paddingTop: 2 }}>
                  {LABELS[k] || k.replace(/_/g, " ")}
                </dt>
                <dd className="min-w-0 flex-1 break-words text-slate-200">
                  {k === "film_link" ? (
                    <a href={v} target="_blank" rel="noreferrer" className="break-all text-ice transition-colors hover:text-white">
                      {v}
                    </a>
                  ) : k === "film_file_url" ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${v}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-ice transition-colors hover:text-white"
                    >
                      Open uploaded file
                    </a>
                  ) : (
                    String(v)
                  )}
                </dd>
              </div>
            ))}
          </dl>

          <div className="flex gap-2">
            <button
              data-testid={`prospect-read-toggle-${p.id}`}
              onClick={() => onPatch(p.id, { is_read: !p.is_read })}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-silver-dim transition-colors hover:border-ice/60 hover:text-ice"
            >
              {p.is_read ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
              Mark {p.is_read ? "Unread" : "Read"}
            </button>
            <button
              data-testid={`prospect-delete-btn-${p.id}`}
              onClick={() => onDelete(p)}
              className="inline-flex items-center gap-1.5 rounded-full border border-crimson/40 px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-crimson-bright transition-colors hover:bg-crimson/10"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          </div>

          <div>
            <p className="eyebrow !text-[9px] mb-2">Scouting notes</p>
            <div className="space-y-2" data-testid={`prospect-notes-${p.id}`}>
              {(p.notes || []).map((n) => (
                <div key={n.id} className="flex items-start justify-between gap-2 rounded-lg bg-navy-700/60 px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-mono text-[9px] uppercase tracking-[0.14em] text-slate-500">
                      {fmtDateTime(n.created_at)}
                    </p>
                    <p className="mt-0.5 break-words text-xs leading-relaxed text-slate-200">{n.text}</p>
                  </div>
                  <button
                    data-testid={`prospect-note-delete-${n.id}`}
                    onClick={() => onDeleteNote(p.id, n.id)}
                    aria-label="Delete note"
                    className="shrink-0 text-slate-500 transition-colors hover:text-crimson-bright"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {(!p.notes || p.notes.length === 0) && (
                <p className="text-xs text-slate-500">No notes yet — log your first evaluation.</p>
              )}
            </div>
            <form onSubmit={addNote} noValidate className="mt-3 flex gap-2">
              <input
                data-testid={`prospect-note-input-${p.id}`}
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Add a scouting note…"
                className={`${api_input} border-white/10 !px-3 !py-2 !text-xs`}
              />
              <button
                type="submit"
                data-testid={`prospect-note-add-btn-${p.id}`}
                className="shrink-0 rounded-lg border border-ice/40 bg-ice/10 px-3 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-ice transition-colors hover:bg-ice hover:text-navy-900"
              >
                Add
              </button>
            </form>
          </div>

          <ProfileForm p={p} onSaved={onSetProfile} />
        </div>
      )}
    </div>
  );
}

function PipelineBoard({ prospects, onPatch, onAddNote, onDeleteNote, onDelete, onSetProfile }) {
  const rated = prospects.filter((p) => p.stars > 0);
  const avg = rated.length ? (rated.reduce((s, p) => s + p.stars, 0) / rated.length).toFixed(1) : "—";
  const committed = prospects.filter((p) => p.stage === "committed").length;
  const stageOf = (key) => STAGES.find((s) => s.key === key) || STAGES[0];

  return (
    <div data-testid="pipeline-board">
      <div className="flex flex-wrap items-center gap-3">
        <span className="rounded-full border border-white/10 bg-navy-850 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300" data-testid="pipeline-stat-total">
          {prospects.length} prospect{prospects.length === 1 ? "" : "s"}
        </span>
        <span className="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-300" data-testid="pipeline-stat-committed">
          {committed} committed
        </span>
        <span className="flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-amber-300" data-testid="pipeline-stat-avg">
          <Star className="h-3 w-3 fill-amber-300 text-amber-300" /> avg {avg}
        </span>
      </div>

      <div className="mt-6 flex gap-4 overflow-x-auto pb-4">
        {STAGES.map((s) => {
          const col = prospects.filter((p) => p.stage === s.key);
          return (
            <div key={s.key} data-testid={`pipeline-column-${s.key}`} className="w-[290px] shrink-0">
              <div className="flex items-center gap-2 border-b border-white/10 pb-3">
                <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                <span className="font-display text-sm font-extrabold uppercase tracking-wider text-white">
                  {s.label}
                </span>
                <span className="ml-auto font-mono text-[10px] text-slate-500">{col.length}</span>
              </div>
              <div className="mt-4 space-y-3">
                {col.map((p) => (
                  <ProspectCard
                    key={p.id}
                    p={p}
                    stageMeta={stageOf(p.stage)}
                    onPatch={onPatch}
                    onAddNote={onAddNote}
                    onDeleteNote={onDeleteNote}
                    onDelete={onDelete}
                    onSetProfile={onSetProfile}
                  />
                ))}
                {col.length === 0 && (
                  <p className="rounded-xl border border-dashed border-white/10 p-6 text-center font-mono text-[10px] uppercase tracking-[0.14em] text-slate-600">
                    No prospects
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function SessionForm({ session, onDone }) {
  const [form, setForm] = useState({
    camp_key: session ? session.camp_key : "showcase",
    description: session ? session.description : "",
    start_date: session ? session.start_date : "",
    end_date: session ? session.end_date : "",
    capacity: session ? session.capacity : 0,
    is_published: session ? session.is_published : false,
  });
  const [sending, setSending] = useState(false);
  const campOption = CAMP_OPTIONS.find((c) => c.key === form.camp_key) || CAMP_OPTIONS[0];

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const payload = {
        camp_key: form.camp_key,
        title: campOption.label,
        badge: campOption.badge,
        description: form.description,
        start_date: form.start_date,
        end_date: form.end_date,
        capacity: Number(form.capacity) || 0,
        is_published: form.is_published,
      };
      if (session) {
        await callWithRefresh(() => api.put(`/admin/camp-sessions/${session.id}`, payload));
        toast.success("Session updated.");
      } else {
        await callWithRefresh(() => api.post("/admin/camp-sessions", payload));
        toast.success("Session created.");
      }
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-testid="session-form" className="glass rounded-2xl p-6">
      <div className="flex items-center justify-between">
        <p className="font-display text-xl font-extrabold uppercase text-white">
          {session ? "Edit session" : "New camp session"}
        </p>
        <button type="button" onClick={onDone} data-testid="session-form-cancel" aria-label="Cancel" className="text-slate-400 transition-colors hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">Camp</span>
          <select
            data-testid="session-form-camp-select"
            value={form.camp_key}
            onChange={set("camp_key")}
            className={`${api_input} appearance-none border-white/10`}
          >
            {CAMP_OPTIONS.map((c) => (
              <option key={c.key} value={c.key} className="bg-navy-800 text-white">
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">
            Spots capacity (0 = unlimited)
          </span>
          <input
            data-testid="session-form-capacity-input"
            type="number"
            min="0"
            value={form.capacity}
            onChange={set("capacity")}
            className={`${api_input} border-white/10`}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">Start date</span>
          <input
            data-testid="session-form-start-input"
            type="date"
            value={form.start_date}
            onChange={set("start_date")}
            className={`${api_input} border-white/10 [color-scheme:dark]`}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">End date (optional)</span>
          <input
            data-testid="session-form-end-input"
            type="date"
            value={form.end_date}
            onChange={set("end_date")}
            className={`${api_input} border-white/10 [color-scheme:dark]`}
          />
        </label>
      </div>
      <label className="mt-5 block">
        <span className="mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-silver-dim">Description</span>
        <textarea
          data-testid="session-form-description-input"
          rows={3}
          value={form.description}
          onChange={set("description")}
          className={`${api_input} resize-none border-white/10`}
        />
      </label>
      <label className="mt-5 flex items-center gap-3">
        <input
          type="checkbox"
          data-testid="session-form-publish-toggle"
          checked={form.is_published}
          onChange={(e) => setForm((f) => ({ ...f, is_published: e.target.checked }))}
          className="h-4 w-4 accent-[#38BDF8]"
        />
        <span className="text-sm text-slate-300">
          Published (visible + bookable on the Camps page — requires a start date)
        </span>
      </label>
      <button type="submit" data-testid="session-form-submit-btn" disabled={sending} className="btn-primary mt-6 !text-sm">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : session ? "Save Changes" : "Create Session"}
      </button>
    </form>
  );
}

function SessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new, object = edit

  const load = useCallback(async () => {
    try {
      const { data } = await callWithRefresh(() => api.get("/admin/camp-sessions"));
      setSessions(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Delete session "${s.title}"?`)) return;
    try {
      await callWithRefresh(() => api.delete(`/admin/camp-sessions/${s.id}`));
      toast.success("Session deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-sessions-tab">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400">
          {sessions.length} session{sessions.length === 1 ? "" : "s"} — published sessions appear on the Camps page
        </p>
        <button data-testid="admin-add-session-btn" onClick={() => setEditing(null)} className="btn-primary !px-5 !py-2.5 !text-sm">
          <Plus className="h-4 w-4" /> New Session
        </button>
      </div>

      {editing !== undefined && <SessionForm session={editing || null} onDone={() => { setEditing(undefined); load(); }} />}

      <div className="space-y-3">
        {sessions.map((s) => (
          <div key={s.id} data-testid={`camp-session-row-${s.id}`} className="rounded-xl border border-white/10 bg-navy-850 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg font-bold uppercase tracking-wide text-white">{s.title}</p>
                <p className="mt-0.5 font-mono text-[11px] text-slate-400">
                  {sessionDateLabel(s)} · {s.capacity > 0 ? `${s.spots_left}/${s.capacity} spots left` : "Open capacity"}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`rounded-full px-3 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] ${
                    s.is_published ? "bg-ice/10 text-ice" : "bg-slate-700/40 text-slate-400"
                  }`}
                >
                  {s.is_published ? "Published" : "Draft"}
                </span>
                <button
                  data-testid={`session-edit-btn-${s.id}`}
                  onClick={() => setEditing(s)}
                  className="rounded-full border border-white/15 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-silver-dim transition-colors hover:border-ice/60 hover:text-ice"
                >
                  Edit
                </button>
                <button
                  data-testid={`session-delete-btn-${s.id}`}
                  onClick={() => remove(s)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-crimson/40 px-4 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-crimson-bright transition-colors hover:bg-crimson/10"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            </div>
            {s.description && <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.description}</p>}
          </div>
        ))}
        {sessions.length === 0 && (
          <p className="rounded-xl border border-dashed border-white/10 p-8 text-center text-sm text-slate-500">
            No sessions yet — create the first one.
          </p>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function Dashboard({ email, onLogout }) {
  const [tab, setTab] = useState("applications");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(
        SUBMISSION_TABS.map((t) =>
          callWithRefresh(() => api.get(`/admin/submissions?type=${t.key}`)).then((r) => [t.key, r.data || []])
        )
      );
      setData(Object.fromEntries(results));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const toggleRead = async (item) => {
    try {
      await callWithRefresh(() =>
        api.post(`/admin/submissions/${tab}/${item.id}/read`, { is_read: !item.is_read })
      );
      setData((d) => ({
        ...d,
        [tab]: d[tab].map((x) => (x.id === item.id ? { ...x, is_read: !item.is_read } : x)),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await callWithRefresh(() => api.delete(`/admin/submissions/${tab}/${item.id}`));
      setData((d) => ({ ...d, [tab]: d[tab].filter((x) => x.id !== item.id) }));
      toast.success("Submission deleted.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const patchProspect = async (id, patch) => {
    try {
      const { data: updated } = await callWithRefresh(() => api.patch(`/admin/prospects/${id}`, patch));
      setData((d) => ({
        ...d,
        prospects: (d.prospects || []).map((x) => (x.id === id ? updated : x)),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setProspectProfile = (updated) => {
    setData((d) => ({
      ...d,
      prospects: (d.prospects || []).map((x) => (x.id === updated.id ? updated : x)),
    }));
  };

  const addProspectNote = async (id, text) => {
    try {
      const { data: updated } = await callWithRefresh(() => api.post(`/admin/prospects/${id}/notes`, { text }));
      setData((d) => ({
        ...d,
        prospects: (d.prospects || []).map((x) => (x.id === id ? updated : x)),
      }));
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const deleteProspectNote = async (id, noteId) => {
    try {
      const { data: updated } = await callWithRefresh(() => api.delete(`/admin/prospects/${id}/notes/${noteId}`));
      setData((d) => ({
        ...d,
        prospects: (d.prospects || []).map((x) => (x.id === id ? updated : x)),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    onLogout();
  };

  const activeMeta = SUBMISSION_TABS.find((t) => t.key === tab);
  const items = data[tab] || [];
  const unread = (key) => (data[key] || []).filter((x) => !x.is_read).length;

  return (
    <div className="mx-auto max-w-6xl px-5 pb-24 pt-28 sm:px-8" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="eyebrow mb-2">Staff inbox</p>
          <h1 className="font-display text-4xl font-black uppercase text-white sm:text-5xl">Every submission.</h1>
          <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-slate-400">
            Signed in as {email}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="admin-refresh-btn"
            onClick={loadAll}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-silver-dim transition-colors hover:border-ice/60 hover:text-ice"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            className="inline-flex items-center gap-2 rounded-full border border-crimson/40 px-5 py-2.5 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-crimson-bright transition-colors hover:bg-crimson/10"
          >
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </div>

      <div className="mt-10 flex flex-wrap gap-2 border-b border-white/10 pb-4">
        {SUBMISSION_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
              tab === t.key ? "bg-ice/10 text-ice" : "text-silver-dim hover:bg-white/5 hover:text-white"
            }`}
          >
            {t.label}
            {unread(t.key) > 0 && (
              <span className="rounded-full bg-crimson px-2 py-0.5 font-mono text-[9px] font-bold text-white">
                {unread(t.key)}
              </span>
            )}
          </button>
        ))}
        <button
          data-testid="admin-tab-camp_sessions"
          onClick={() => setTab("camp_sessions")}
          className={`flex items-center gap-2 rounded-full px-4 py-2 font-display text-sm font-bold uppercase tracking-wider transition-colors ${
            tab === "camp_sessions" ? "bg-ice/10 text-ice" : "text-silver-dim hover:bg-white/5 hover:text-white"
          }`}
        >
          Camp Sessions
        </button>
      </div>

      <div className="mt-8">
        {tab === "camp_sessions" ? (
          <SessionsTab />
        ) : tab === "prospects" ? (
          <PipelineBoard
            prospects={items}
            onPatch={patchProspect}
            onAddNote={addProspectNote}
            onDeleteNote={deleteProspectNote}
            onDelete={remove}
            onSetProfile={setProspectProfile}
          />
        ) : loading ? (
          <div className="grid place-items-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-dashed border-white/10 p-10 text-center text-sm text-slate-500">
            Nothing here yet — new {activeMeta.label.toLowerCase()} arrive from the website forms.
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <SubmissionCard
                key={item.id}
                type={tab}
                item={item}
                meta={activeMeta}
                onToggleRead={toggleRead}
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function AdminPage() {
  const [authed, setAuthed] = useState(null); // null = checking

  useEffect(() => {
    api
      .get("/auth/me")
      .then((r) => setAuthed(r.data.email))
      .catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return (
      <div className="grid min-h-screen place-items-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  if (!authed) {
    return <LoginScreen onAuthed={setAuthed} />;
  }
  return <Dashboard email={authed} onLogout={() => setAuthed(false)} />;
}
